// Create / edit a completion policy: name, mode (all required · any one of)
// and requirement rows (kind + threshold where the kind takes one; for
// system_action, Phase 6, an optional platform event from GET /system-events
// — absent means any verified platform activity). Emits a
// CompletionPolicyInput; the caller decides whether that becomes POST or
// PATCH. Only structural checks happen here (validatePolicyInput) — whether a
// learner meets a policy is always the server's evaluation.
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import type { CompletionPolicy, CompletionPolicyInput, PolicyMode, PolicyRequirement, RequirementKind } from '@/services/learningService';
import { REQUIREMENT_KINDS } from '@/services/learningService';
import { useSystemEvents } from '../api/hooks';
import { isSystemKind, kindRequiresThreshold, kindTakesThreshold, requirementHelp, requirementLabel, validatePolicyInput } from '../lib/completion';

const selectCls = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';
const chipCls = (on: boolean) => `rounded-full border px-2.5 py-1 text-xs font-medium transition ${on ? 'border-primary-500 bg-primary-500/15 text-primary-700 dark:text-primary-300' : 'border-border text-muted-foreground hover:bg-muted'}`;

interface Row { key: string; kind: RequirementKind; threshold: string; event_type: string }
const newRow = (kind: RequirementKind = 'attendance'): Row => ({ key: Math.random().toString(36).slice(2), kind, threshold: '', event_type: '' });

interface Props {
  initial?: CompletionPolicy | null;
  onSave: (input: CompletionPolicyInput) => void;
  onCancel: () => void;
  busy?: boolean;
  idPrefix?: string;
  className?: string;
}

export const PolicyEditor: React.FC<Props> = ({ initial, onSave, onCancel, busy, idPrefix = 'pe', className }) => {
  const t = useT();
  const [name, setName] = useState(initial?.name || '');
  const [mode, setMode] = useState<PolicyMode>(initial?.mode || 'all_required');
  const [rows, setRows] = useState<Row[]>(initial?.requirements?.length
    ? initial.requirements.map((r) => ({ ...newRow(r.kind), threshold: r.threshold != null ? String(r.threshold) : '', event_type: isSystemKind(r.kind) ? r.event_type || '' : '' }))
    : [newRow()]);
  const [errs, setErrs] = useState<string[]>([]);
  const patch = (key: string, p: Partial<Row>) => setRows((xs) => xs.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const unused = REQUIREMENT_KINDS.filter((k) => !rows.some((r) => r.kind === k));
  // The editor is a learning:manage surface; the catalog is only fetched once a system_action row exists.
  const events = useSystemEvents(rows.some((r) => isSystemKind(r.kind)));
  const catalog = events.data || [];

  const save = () => {
    const input: CompletionPolicyInput = {
      name: name.trim(), mode,
      requirements: rows.map((r) => {
        const req: PolicyRequirement = { kind: r.kind };
        // A score row with an empty threshold is sent without one so the structural check flags it.
        if (kindTakesThreshold(r.kind) && r.threshold.trim() !== '') req.threshold = Number(r.threshold);
        // event_type only ever rides on a system_action row (the server 400s it elsewhere).
        if (isSystemKind(r.kind) && r.event_type) req.event_type = r.event_type;
        return req;
      }),
    };
    const problems = validatePolicyInput(t, input);
    setErrs(problems);
    if (problems.length) return;
    onSave(input);
  };

  return (
    <div className={`space-y-3 ${className || ''}`} data-testid="policy-editor">
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-name`} className="text-xs">{t('training:policy_name', 'Policy name')}</Label>
          <Input id={`${idPrefix}-name`} className="h-9" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('training:policy_name_ph', 'e.g. Attend + trainer sign-off')} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <div className="text-xs font-medium">{t('training:policy_mode', 'How requirements combine')}</div>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('training:policy_mode', 'How requirements combine')}>
            <button type="button" role="radio" aria-checked={mode === 'all_required'} className={chipCls(mode === 'all_required')} onClick={() => setMode('all_required')}>{t('training:policy_mode_all', 'All required')}</button>
            <button type="button" role="radio" aria-checked={mode === 'any_of'} className={chipCls(mode === 'any_of')} onClick={() => setMode('any_of')}>{t('training:policy_mode_any', 'Any one of')}</button>
          </div>
          <p className="text-[11px] text-muted-foreground">{mode === 'any_of' ? t('training:policy_mode_any_help', 'The assignment completes as soon as one requirement is met.') : t('training:policy_mode_all_help', 'Every requirement must be met before the assignment completes.')}</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-medium">{t('training:policy_requirements', 'Requirements')}</div>
        {rows.map((r, i) => (
          <div key={r.key} className="rounded-md border border-border p-2 space-y-1" data-testid={`policy-row-${i}`}>
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1 flex-1 min-w-[10rem]">
                <Label htmlFor={`${idPrefix}-kind-${i}`} className="text-xs">{t('training:req_kind', 'Requirement')}</Label>
                <select id={`${idPrefix}-kind-${i}`} className={selectCls} value={r.kind} disabled={busy} onChange={(e) => patch(r.key, { kind: e.target.value as RequirementKind, threshold: kindTakesThreshold(e.target.value) ? r.threshold : '', event_type: isSystemKind(e.target.value) ? r.event_type : '' })}>
                  {REQUIREMENT_KINDS.map((k) => <option key={k} value={k} disabled={k !== r.kind && rows.some((x) => x.kind === k)}>{requirementLabel(t, k)}</option>)}
                </select>
              </div>
              {kindTakesThreshold(r.kind) && (
                <div className="space-y-1 w-32">
                  <Label htmlFor={`${idPrefix}-th-${i}`} className="text-xs">{kindRequiresThreshold(r.kind) ? t('training:threshold', 'Threshold (%)') : t('training:threshold_optional', 'Threshold (%, optional)')}</Label>
                  <Input id={`${idPrefix}-th-${i}`} className="h-9" type="number" min={0} max={100} value={r.threshold} disabled={busy} onChange={(e) => patch(r.key, { threshold: e.target.value })} />
                </div>
              )}
              {isSystemKind(r.kind) && (
                <div className="space-y-1 flex-1 min-w-[12rem]">
                  <Label htmlFor={`${idPrefix}-ev-${i}`} className="text-xs">{t('training:req_event_type', 'Platform event (optional)')}</Label>
                  <select id={`${idPrefix}-ev-${i}`} className={selectCls} value={r.event_type} disabled={busy || events.isLoading} onChange={(e) => patch(r.key, { event_type: e.target.value })}>
                    <option value="">{t('training:req_event_any', 'Any verified platform activity')}</option>
                    {catalog.map((e) => <option key={e.event_type} value={e.event_type}>{e.label} · {e.source_service}</option>)}
                  </select>
                </div>
              )}
              <button type="button" aria-label={t('training:req_remove', 'Remove requirement {{n}}', { n: i + 1 })} className="mb-2 text-muted-foreground hover:text-red-500 disabled:opacity-50" disabled={busy || rows.length === 1} onClick={() => setRows((xs) => xs.filter((x) => x.key !== r.key))}><Trash2 className="h-4 w-4" /></button>
            </div>
            <p className="text-[11px] text-muted-foreground" data-testid={`policy-help-${i}`}>{requirementHelp(t, r.kind)}</p>
          </div>
        ))}
        <Button size="sm" variant="outline" disabled={busy || unused.length === 0} onClick={() => setRows((xs) => [...xs, newRow(unused[0])])} data-testid={`${idPrefix}-add`}>
          <Plus className="mr-1 h-4 w-4" />{t('training:req_add', 'Add requirement')}
        </Button>
      </div>

      {errs.length > 0 && (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 dark:bg-red-500/10 p-2 text-xs text-red-700 dark:text-red-300">
          <ul className="list-disc pl-4 space-y-0.5">{errs.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>{t('training:cancel', 'Cancel')}</Button>
        <Button size="sm" onClick={save} disabled={busy} data-testid={`${idPrefix}-save`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:save', 'Save')}</Button>
      </div>
    </div>
  );
};

export default PolicyEditor;
